import { NumberFormats as nf, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { LabelFormat, Progress } from '../types';

type ProgressLabelProps = {
  progress: Progress;
  format: LabelFormat;
  formatAsCurrency?: boolean;
};

/**
 * Helper component for formatting common progress bar labels.
 */
function ProgressLabel({ progress, format, formatAsCurrency = false }: ProgressLabelProps) {
  const t = useTranslations('offers');
  const formatCurrency = useCurrencyFormatter();

  const formatValueAsCurrency = (value: number) => {
    if (!formatAsCurrency) return value;

    if (format === 'currentOfMax') {
      return formatCurrency(value).format(nf.WITH_DECIMALS).get();
    }

    return formatCurrency(value * 100)
      .format(nf.WITH_DECIMALS)
      .get();
  };

  const remaining = progress.max - progress.current;
  const remainingValue = formatValueAsCurrency(remaining);
  const currentValue = formatValueAsCurrency(progress.current);
  const maxValue = formatValueAsCurrency(progress.max);

  switch (format) {
    case 'percentageComplete':
      return <span>{`${Math.round((100 / progress.max) * progress.current)}%`}</span>;
    case 'remainingCount':
      return <span>{remainingValue}</span>;
    case 'remainingOfMax':
      return <span>{`${remainingValue} ${t('of')} ${maxValue}`}</span>;
    case 'currentOfMax':
      return <span>{`${currentValue} ${t('of')} ${maxValue}`}</span>;
    default:
      throw new Error(`Unknown label format: ${format}`);
  }
}

export { ProgressLabel };
